import React from 'react';
import type { ValidationError } from '@/utils/validation/types';
import { TravelConfigForm } from '@/components/config_forms/TravelConfigForm';
import type { ClientFormData } from '../new_client/utilities/formDataUtils';
import { getFieldErrorElement } from '@/utils/validation/helpers';
import type { ExpenseCategory } from '@/types/ExpenseCategory';

interface ClientTravelConfigProps {
  formData: ClientFormData;
  handleChange: (field: keyof ClientFormData, value: unknown) => void;
  handleSwitchChange: (field: keyof ClientFormData, checked: boolean) => void;
  onFieldBlur?: (field: keyof ClientFormData) => void;
  onClearFieldValidation?: (field: keyof ClientFormData) => void;
  isLoading?: boolean;
  errors?: ValidationError[];
  touchedFields?: Set<string>;
  isTravelManuallyEnabled: boolean;
  setIsTravelManuallyEnabled: (enabled: boolean) => void;
  isCustomized?: boolean;
  onRevert?: () => void;
}

export const ClientTravelConfig: React.FC<ClientTravelConfigProps> = ({
  formData,
  handleChange,
  handleSwitchChange,
  onFieldBlur,
  onClearFieldValidation,
  isLoading,
  errors = [],
  touchedFields = new Set(),
  isTravelManuallyEnabled,
  setIsTravelManuallyEnabled,
  isCustomized,
  onRevert,
}) => {
  const {
    per_diem_base_rate,
    travel_day_rate_multiplier,
    multiplier_applies_to_overages,
    uses_gsa_rate,
    apply_per_diem,
    travel_distance_threshold,
    expense_categories
  } = formData;

  // Infer if travel is enabled based on whether relevant fields have values, or it was manually toggled on.
  const travelEnabled = isTravelManuallyEnabled || apply_per_diem || per_diem_base_rate != null || travel_day_rate_multiplier != null || !!uses_gsa_rate;

  // Travel expense categories: filter travel-scoped from all categories
  const allCategories = (expense_categories as ExpenseCategory[]) ?? [];
  const travelCategories = allCategories.filter((c) => c.scope === 'travel_only');

  const handleTravelCategoriesChange = (updated: ExpenseCategory[]) => {
    const allScope = allCategories.filter((c) => c.scope === 'all');
    handleChange('expense_categories', [...allScope, ...updated]);
  };

  return (
    <TravelConfigForm
      title="Travel Policies"
      description="Configure travel days and per diem rates for this client."
      needsTravel={travelEnabled}
      applyPerDiem={apply_per_diem ?? false}
      usesGsaRate={uses_gsa_rate ?? false}
      multiplierAppliesToOverages={multiplier_applies_to_overages || false}
      onNeedsTravelChange={(checked: boolean) => {
        if (!checked) {
          handleChange('per_diem_base_rate', null);
          handleChange('travel_day_rate_multiplier', null);
          handleSwitchChange('multiplier_applies_to_overages', false);
          handleSwitchChange('uses_gsa_rate', false);
          handleSwitchChange('apply_per_diem', false);
          handleChange('travel_distance_threshold', null);
          setIsTravelManuallyEnabled(false);
        } else {
          setIsTravelManuallyEnabled(true);
          handleSwitchChange('apply_per_diem', true);
        }
      }}
      onApplyPerDiemChange={(checked: boolean) => {
        handleSwitchChange('apply_per_diem', checked);
        if (!checked) {
          handleChange('per_diem_base_rate', null);
          handleSwitchChange('uses_gsa_rate', false);
        }
      }}
      onUsesGsaRateChange={(checked: boolean) => {
        handleSwitchChange('uses_gsa_rate', checked);
        if (checked) onClearFieldValidation?.('per_diem_base_rate');
      }}
      onMultiplierAppliesToOveragesChange={(checked: boolean) => {
        handleSwitchChange('multiplier_applies_to_overages', checked);
      }}
      perDiemInputProps={{
        value: per_diem_base_rate ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('per_diem_base_rate', e.target.value ? parseFloat(e.target.value) : null),
        id: 'per_diem_base_rate',
        onBlur: () => onFieldBlur?.('per_diem_base_rate'),
        disabled: isLoading,
        'data-validation-key': 'per_diem_base_rate',
      }}
      travelMultiplierInputProps={{
        value: travel_day_rate_multiplier ?? '',
        id: 'travel_day_rate_multiplier',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('travel_day_rate_multiplier', e.target.value ? parseFloat(e.target.value) : null),
        onBlur: () => onFieldBlur?.('travel_day_rate_multiplier'),
        disabled: isLoading,
        'data-validation-key': 'travel_day_rate_multiplier',
      }}
      perDiemError={getFieldErrorElement('per_diem_base_rate', errors, touchedFields)}
      travelMultiplierError={getFieldErrorElement('travel_day_rate_multiplier', errors, touchedFields)}
      travelDistanceThreshold={travel_distance_threshold ?? null}
      onTravelDistanceThresholdChange={(value) => handleChange('travel_distance_threshold', value)}
      travelDistanceThresholdInputProps={{
        value: travel_distance_threshold ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('travel_distance_threshold', e.target.value ? parseInt(e.target.value, 10) : null),
        id: 'travel_distance_threshold',
        onBlur: () => onFieldBlur?.('travel_distance_threshold'),
        disabled: isLoading,
      }}
      travelDistanceThresholdError={getFieldErrorElement('travel_distance_threshold', errors, touchedFields)}
      travelExpenseCategories={travelCategories}
      onTravelExpenseCategoriesChange={handleTravelCategoriesChange}
      isCustomized={isCustomized}
      onRevert={onRevert}
      context="client"
    />
  );
};
